import { AxiosRequestConfig, AxiosResponse } from 'axios';

import { appConfig } from 'constants/appConfig';
import { alphaSessionToken } from 'services/alpha/alphaSession';
import axiosInstance from 'services/axiosInstance';
import {
  BEHAVIOUR_PATH,
  LIVE_ASSIST_STATUSES,
  LiveAssistEvent,
  LiveAssistFeedback,
  LiveAssistStatus,
  parseLiveAssistChunk,
} from 'components/Alpha/LiveAssist/liveAssistContract';

export type LiveAssistStreamOutcome =
  | { kind: 'closed'; received: number }
  | { kind: 'refused'; status: number }
  | { kind: 'busy'; retryAfterMs: number }
  | { kind: 'failed'; received: number }
  | { kind: 'aborted' };

export interface LiveAssistStreamHandlers {
  onOpen: () => void;
  onEvent: (event: LiveAssistEvent) => void;
}

const REFUSED_STATUSES = [401, 403, 404];
const DEFAULT_RETRY_AFTER_SECONDS = 30;

const apiUrl = (path: string): string =>
  `${(appConfig.baseUrl ?? '').replace(/\/$/, '')}/${path.replace(/^\//, '')}`;

const isAbort = (error: unknown): boolean => (error as { name?: string })?.name === 'AbortError';

type ReadResult = { value?: Uint8Array; done: boolean } | 'idle';

const readWithin = async (
  reader: ReadableStreamDefaultReader<Uint8Array>,
  idleMs: number,
): Promise<ReadResult> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const idle = new Promise<'idle'>(resolve => {
    timer = setTimeout(() => {
      resolve('idle');
    }, idleMs);
  });
  try {
    return await Promise.race([reader.read(), idle]);
  } finally {
    clearTimeout(timer);
  }
};

export const openLiveAssistStream = async (
  path: string,
  handlers: LiveAssistStreamHandlers,
  signal: AbortSignal,
  idleMs: number,
): Promise<LiveAssistStreamOutcome> => {
  let received = 0;
  try {
    const res = await fetch(apiUrl(path), {
      method: 'GET',
      signal,
      headers: {
        Accept: 'text/event-stream',
        Authorization: `Bearer ${alphaSessionToken() ?? ''}`,
      },
    });
    if (REFUSED_STATUSES.includes(res.status)) return { kind: 'refused', status: res.status };
    if (res.status === 429) {
      const seconds = Number(res.headers.get('Retry-After')) || DEFAULT_RETRY_AFTER_SECONDS;
      return { kind: 'busy', retryAfterMs: seconds * 1000 };
    }
    if (!res.ok || res.body === null) return { kind: 'failed', received };
    handlers.onOpen();
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const result = await readWithin(reader, idleMs);
      if (result === 'idle') {
        reader.cancel().catch(() => undefined);
        return { kind: 'failed', received };
      }
      if (result.done) return { kind: 'closed', received };
      buffer += decoder.decode(result.value, { stream: true });
      const parsed = parseLiveAssistChunk(buffer);
      buffer = parsed.rest;
      received += parsed.events.length;
      parsed.events.forEach(handlers.onEvent);
    }
  } catch (error) {
    if (isAbort(error) || signal.aborted) return { kind: 'aborted' };
    return { kind: 'failed', received };
  }
};

const post = async (path: string, data?: unknown): Promise<unknown> => {
  const config = { method: 'POST', url: path, data, rejectWithResponse: true };
  const res: AxiosResponse<unknown> | undefined = await axiosInstance.request(
    config as AxiosRequestConfig,
  );
  return res?.data;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const serverMessage = (error: unknown): string | undefined => {
  const response = (error as { response?: { status?: number; data?: { message?: unknown } } })
    ?.response;
  const status = response?.status ?? 0;
  const message = response?.data?.message;
  return status >= 400 && status < 500 && typeof message === 'string' && message.trim() !== ''
    ? message
    : undefined;
};

export const serverCode = (error: unknown): string | undefined => {
  const code = (error as { response?: { data?: { code?: unknown } } })?.response?.data?.code;
  return typeof code === 'string' && code !== '' ? code : undefined;
};

export const postLiveAssistAsk = async (
  path: string,
  text: string,
  clientAskId: string,
  streamId?: string,
): Promise<{ askId?: string }> => {
  const body = streamId === undefined ? { text, clientAskId } : { text, clientAskId, streamId };
  const data = await post(path, body);
  const askId = isRecord(data) ? data.askId : undefined;
  if (typeof askId === 'string' && askId !== '') return { askId };
  if (typeof askId === 'number') return { askId: String(askId) };
  return {};
};

export const getLiveAssistReplayEnabled = async (): Promise<boolean> => {
  const config = { method: 'GET', url: BEHAVIOUR_PATH, rejectWithResponse: true };
  try {
    const res: AxiosResponse<unknown> | undefined = await axiosInstance.request(
      config as AxiosRequestConfig,
    );
    const data = res?.data;
    return isRecord(data) && data.liveAssistReplayEnabled === true;
  } catch {
    return false;
  }
};

export const postLiveAssistFeedback = async (
  path: string,
  action: LiveAssistFeedback,
): Promise<void> => {
  await post(path, { action });
};

export const postLiveAssistCommand = async (
  path: string,
): Promise<LiveAssistStatus | undefined> => {
  const data = await post(path);
  const status = isRecord(data) ? data.status : undefined;
  return LIVE_ASSIST_STATUSES.find(item => item === status);
};
